import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import moment from "moment";
const BASER_URL = '/master'
interface Filter {
    filterField: string;
    filterValue: any;
}

interface FetchCustomersParams {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: Filter[];
}

interface FetchCustomersResponse {
    data: any[];
    rowCount: number;
}

export const fetchAllCustomers = createAsyncThunk<
    FetchCustomersResponse,
    FetchCustomersParams
>(
    "customer/fetchAllCustomers",
    async ({ page, pageSize, sort, order, search, filters }) => {
        const params = new URLSearchParams();
        params.append("page", page.toString());
        params.append("page_size", pageSize.toString());

        if (search) params.append("search", search);
        if (sort) {
            params.append("ordering", order === "desc" ? `-${sort}` : sort);
        }

        filters?.forEach(({ filterField, filterValue }) => {
            if (!filterField || filterValue === undefined || filterValue === null) return;

            // Check if filterValue is an object (e.g., date range)
            if (typeof filterValue === "object" && filterValue !== null) {
                // If it's a date filter
                if (filterValue.from_date || filterValue.to_date) {
                    if (filterField === "created_at") {
                        if (filterValue.from_date)
                            params.append("created_at_from", String(filterValue.from_date));
                        if (filterValue.to_date)
                            params.append("created_at_to", String(filterValue.to_date));
                    } else if (filterField === "updated_at") {
                        if (filterValue.from_date)
                            params.append("updated_at_from", String(filterValue.from_date));
                        if (filterValue.to_date)
                            params.append("updated_at_to", String(filterValue.to_date));
                    }
                } else {
                    // For other object filters
                    Object.entries(filterValue).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && value !== "") {
                            params.append(key, String(value));
                        }
                    });
                }
            } else {
                // Normal simple filters
                params.append(filterField, String(filterValue));
            }
        });


        console.log(params.toString())

        const response = await Axios.get(`${BASER_URL}/customer/?${params.toString()}`);

        return {
            data: response?.data?.data ?? [],
            rowCount: response?.data?.count ?? 0,
        };
    }
);


export const fetchCustomerById = createAsyncThunk(
    "customer/fetchCustomerById",
    async (id: string, { rejectWithValue }) => {
        try {
            const response: any = await Axios.get(`${BASER_URL}/customer/${id}/get/`);
            return response.data.data || response.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch Customer by ID"
            );
        }
    }
);

export const addCustomer = createAsyncThunk(
    "customer/addCustomer",
    async (formData: FormData, { rejectWithValue }) => {
        try {
            const response: any = await Axios.post(`${BASER_URL}/customer/create/`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Failed to add Customer"
            );
        }
    }
);

export const updateCustomer = createAsyncThunk(
    "customer/updateCustomer",
    async ({ id, formData }: { id: string; formData: FormData }, { rejectWithValue }) => {
        try {
            const response: any = await Axios.put(`${BASER_URL}/customer/${id}/update/`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Failed to update Customer"
            );
        }
    }
);

export const updateStatusInCustomer = createAsyncThunk(
    "customer/updateStatusInCustomer",
    async (data: { id: string; is_active: boolean }, { rejectWithValue }) => {
        try {
            const payload = { is_active: data.is_active };
            const response: any = await Axios.patch(
                `${BASER_URL}/customer/${data.id}/update/`,
                payload
            );
            const responseData = response.data?.data || response.data || {};
            // Ensure id and is_active are included in the response
            return {
                ...responseData,
                id: responseData.id || responseData.customer_id || data.id,
                is_active: responseData.is_active !== undefined ? responseData.is_active : data.is_active,
            };
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data || "Failed to update Customer status"
            );
        }
    }
);





export const exportCustomerCSV = async ({
    search,
    filters,
}: {
    search?: string;
    filters?: any;
}) => {
    try {
        const params: any = {};

        if (search) {
            params.search = search;
        }

        if (filters && filters.length > 0) {
            filters.forEach(
                ({
                    filterField,
                    filterValue,
                }: {
                    filterField: string;
                    filterValue: any;
                }) => {
                    if (filterField && filterValue !== undefined && filterValue !== null) {
                        if (typeof filterValue === "object" && filterValue !== null) {
                            Object.entries(filterValue).forEach(([key, value]) => {
                                if (value !== undefined && value !== null && value !== "") {
                                    params[key] = String(value);
                                }
                                if (
                                    (filterValue.from_date || filterValue.to_date) &&
                                    (filterField === "created_at" || filterField === "updated_at")
                                ) {
                                    if (filterField === "created_at") {
                                        filterField = "created";
                                    } else {
                                        filterField = "updated";
                                    }
                                    params.filter_by = filterField;
                                }
                            });
                        } else {
                            params[filterField] = filterValue;
                        }
                    }
                }
            );
        }

        // Try to get CSV blob first
        try {
            const response = await Axios.get(`${BASER_URL}/customer/export/`, {
                params,
                responseType: "blob",
            });

            return {
                blob: response.data,
                filename: `customers_${moment().format("YYYY-MM-DD")}.csv`,
            };
        } catch (blobError) {
            // If blob export fails, try JSON and convert to CSV
            const response = await Axios.get(`${BASER_URL}/customer/`, {
                params: { ...params, export: "excel" },
            });

            const data = response.data?.data || response.data || [];

            // Convert JSON to CSV
            if (data.length === 0) {
                throw new Error("No data to export");
            }

            // Define CSV headers based on Customer structure
            const headers = [
                "Sr No.",
                "Customer Name",
                "Company Name",
                "Customer Type",
                "Email",
                "Contact Number",
                "Aadhar No.",
                "GSTIN",
                "Status",
                "Contact Person Name",
                "Contact Person Mobile No",
                "Address",
                "State",
                "City",
                "Country",
                "Pincode",
                "Created By",
                "Created Date",
                "Updated By",
                "Updated Date",
            ];

            // Convert data to CSV rows
            const csvRows = [
                headers.join(","),
                ...data.map((customer: any, index: number) => {
                    const row = [
                        customer.sr_no || index + 1,
                        customer.customer_name || "",
                        customer.company_name || "",
                        customer.customer_type || "",
                        customer.email || "",
                        customer.contact_number || "",
                        customer.aadhar_no || "",
                        customer.gstin || "",
                        customer.is_active ? "Active" : "Inactive",
                        customer.contact_person_name || "",
                        customer.contact_person_mobile_no || "",
                        customer.address || "",
                        customer.state || "",
                        customer.city || "",
                        customer.country || "",
                        customer.pincode || "",
                        customer.created_by_user?.username || customer.created_by || "",
                        customer.created_at ? moment(customer.created_at).format("DD/MM/YYYY") : "",
                        customer.updated_by_user?.username || customer.updated_by || "",
                        customer.updated_at ? moment(customer.updated_at).format("DD/MM/YYYY") : "",
                    ];
                    // Escape commas and quotes in CSV
                    return row.map((cell) => {
                        const cellStr = String(cell || "");
                        if (cellStr.includes(",") || cellStr.includes('"') || cellStr.includes("\n")) {
                            return `"${cellStr.replace(/"/g, '""')}"`;
                        }
                        return cellStr;
                    }).join(",");
                }),
            ];

            const csvContent = csvRows.join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

            return {
                blob,
                filename: `customers_${moment().format("YYYY-MM-DD")}.csv`,
            };
        }
    } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message || "Failed to export customers");
    }
};
